import { Suspense, useDeferredValue } from 'react'
import { createFileRoute, useMatch } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { SETTINGS_STACK } from './-components/settings/settings-section'
import { formatCompact, formatCount, formatMs, formatPct } from './-components/admin/metrics-format'
import { AssistantPending, AssistantSkeleton, ChartSkeleton, TILE_GRID } from './-components/admin/metrics-pending'
import {
  EmptyPanel,
  MetricsSection,
  SMALL_PLOT,
  TelemetryNotice,
  Tile,
  TurnsChart,
} from './-components/admin/metrics-panels'

// The in-app assistant's turns over the window: how many, how many failed and why, how long and how costly they were,
// and which tools they called.

export const Route = createFileRoute('/_app/admin/metrics/assistant')({
  component: RouteComponent,
  pendingComponent: AssistantPending,
})

function useWindowContext() {
  return useMatch({ from: '/_app/admin/metrics/assistant/_window' }).context
}

function RouteComponent() {
  return (
    <div className={SETTINGS_STACK}>
      <MetricsSection copy="assistant">
        <Suspense fallback={<AssistantSkeleton />}>
          <AssistantPanel />
        </Suspense>
      </MetricsSection>
    </div>
  )
}

function AssistantPanel() {
  const context = useWindowContext()
  const options = useDeferredValue(context.metricsAssistantQueryOptions)
  const { data } = useSuspenseQuery(options)
  const stale = options !== context.metricsAssistantQueryOptions
  const offline = data.clickhouse !== 'ok'
  if (!offline && data.turns.value === 0 && data.turns.previous === 0)
    return <EmptyPanel>Nobody asked the assistant anything in this window.</EmptyPanel>
  const maxCalls = Math.max(1, ...data.tools.map((t) => t.calls))
  return (
    <div className={cn('flex flex-col gap-3 transition-opacity', stale && 'opacity-60')}>
      <TelemetryNotice state={data.clickhouse} />
      <div className={TILE_GRID}>
        <Tile label="Turns" figure={data.turns} format={formatCount} better="up" offline={offline} />
        <Tile label="Failed turns" figure={data.errorPct} format={formatPct} better="down" offline={offline} />
        <Tile label="Turn p95" figure={data.p95Ms} format={formatMs} better="down" offline={offline} />
        <div className="bg-card flex min-h-[92px] flex-col gap-0.5 rounded-lg border p-3">
          <span className="text-muted-foreground truncate text-xs">Tokens per turn</span>
          <span className="text-xl font-semibold tracking-tight tabular-nums sm:text-2xl">
            {formatCompact(data.inputTokensPerTurn)}
            <span className="text-muted-foreground text-sm font-normal"> in · </span>
            {formatCompact(data.outputTokensPerTurn)}
            <span className="text-muted-foreground text-sm font-normal"> out</span>
          </span>
          <span className="text-muted-foreground truncate text-xs">
            uncached · {data.modelCallsPerTurn ?? '—'} model calls a turn · {formatCount(data.users)} users
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Suspense fallback={<ChartSkeleton plot={SMALL_PLOT + 40} />}>
          <Turns />
        </Suspense>
        <div className="flex min-w-0 flex-col gap-3 rounded-lg border p-4">
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold">Tools</h3>
            <p className="text-muted-foreground text-xs">Calls per tool, and in how many turns it ran.</p>
          </div>
          {data.tools.length === 0 ? (
            <p className="text-muted-foreground text-xs">No tool was called.</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {data.tools.map((tool) => (
                <li key={tool.tool} className="relative flex h-7 items-center justify-between gap-3 px-2 text-xs">
                  <span
                    className="absolute inset-y-0 left-0 rounded-sm bg-(--series-1)/15"
                    style={{ width: `${(tool.calls / maxCalls) * 100}%` }}
                  />
                  <span className="relative truncate font-mono">{tool.tool}</span>
                  <span className="text-muted-foreground relative shrink-0 tabular-nums">
                    {formatCount(tool.calls)} · {formatCount(tool.turns)} turns
                  </span>
                </li>
              ))}
            </ul>
          )}
          {data.errorKinds.length > 0 && (
            <p className="text-muted-foreground text-xs">
              Failures:{' '}
              {data.errorKinds.map((e, i) => (
                <span key={e.kind}>
                  {i > 0 && ', '}
                  <span className="text-foreground font-mono">{e.kind}</span> {formatCount(e.count)}
                </span>
              ))}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function Turns() {
  const context = useWindowContext()
  const options = useDeferredValue(context.metricsTrafficQueryOptions)
  const { data } = useSuspenseQuery(options)
  return <TurnsChart data={data} height={SMALL_PLOT + 40} />
}
